import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { useDiagramInfo } from './useDiagramInfo';
import { useFolderData } from './useFolderData';
import { useCompassGraph } from './useCompassGraph';
import { compassGraphToImportResult } from '../domains/network/interop/compassGraphToImportResult';
import { hydrateImportedResources } from '../domains/network/interop/hydrateImportedResources';
import { getCommandManager } from './useCommandManager';
import { getNodeMutationManager } from '../core/commands/mutations/NodeMutationManager';
import { Logger } from '../utils/logger';

const log = Logger.getLoggerWithPrefix('useNetworkDesignSync');

/**
 * Y.Map name + key used to mark a design diagram as hydrated. Persists
 * across collaborators so a second client opening the same diagram
 * doesn't re-seed it on top of the first client's work.
 *
 *   doc.getMap('networkDesignMeta').get('hydrated')   // true | undefined
 *   doc.getMap('networkDesignMeta').get('hydratedAt') // ISO timestamp
 *   doc.getMap('networkDesignMeta').get('baselineFolderId') // source folder
 *
 * Stored separately from `forkMetadata` (the orgchart fork pattern) so
 * the schemas don't fight. Network-design forks reference a *folder*
 * (not a parent diagram), so the field set is different.
 */
const META_MAP = 'networkDesignMeta';
const META_HYDRATED = 'hydrated';
const META_HYDRATED_AT = 'hydratedAt';
const META_BASELINE_FOLDER_ID = 'baselineFolderId';

export interface UseNetworkDesignSyncResult {
  isDesign: boolean;
  /** True while the baseline graph is loading OR seed is being applied. */
  isHydrating: boolean;
  /** True iff the underlying Y.Doc carries the `hydrated` sentinel. */
  isHydrated: boolean;
  /** Surfaces graph-fetch errors. Hydration failures are logged. */
  error: unknown;
}

/**
 * useNetworkDesignSync — design-mode first-open hydration.
 *
 * Activates for diagrams whose `viewDefinition.type === 'network-design'`.
 * On first connection:
 *
 *   1. Wait for Y.Doc to be attached (mutation manager is plumbed by
 *      the existing realtime path).
 *   2. Read the `networkDesignMeta.hydrated` sentinel inside the Y.Doc.
 *      If present, the design has already been seeded by an earlier
 *      session (or another collaborator) — no-op.
 *   3. Fetch the baseline folder graph via the standard
 *      `useCompassGraph` query, convert it to an `ImportResult` via
 *      `compassGraphToImportResult`, and hand it to
 *      `hydrateImportedResources`. That helper dispatches
 *      `CreateNetworkResourceCommand` / `CreateNetworkConnectorCommand`
 *      through `CommandManager` — every node lands in Y.Doc through
 *      the same mutation path as a regular import, so persistence and
 *      collaboration just work.
 *   4. Set the sentinel inside a Y.Doc transaction so subsequent opens
 *      and concurrent collaborators see it.
 *
 * Differences from `useCompassExplorerSync` (the precedent):
 *
 *   - Explorer keeps the Y.Doc deliberately empty and `loadScene()`s a
 *     synthesised scene each render. Design diagrams treat the Y.Doc
 *     as authoritative — the seed is *persisted* and every subsequent
 *     edit flows through the normal Yjs sync path.
 *   - Explorer recomputes on every focus/zoom change; design hydration
 *     is single-shot, gated by the sentinel.
 *
 * Concurrency: two clients opening the design at the same moment race.
 * Each independently checks `networkDesignMeta.hydrated`; both may
 * observe `false`. The mitigation is the `shapes.size > 0` short-
 * circuit before the hydration call — if either client gets a
 * non-empty shapes map back from Yjs sync, it skips. The remaining
 * (rare) collision case produces duplicate resources, mitigated in
 * DL-5 when re-fork lands and we introduce idempotent ids per
 * baseline-resource-id.
 *
 * Returns hydration status so the canvas chrome can render a
 * "Seeding from baseline…" indicator instead of a flash of empty.
 */
export function useNetworkDesignSync(diagramId: string | undefined): UseNetworkDesignSyncResult {
  const { data: diagramInfo } = useDiagramInfo(diagramId);
  const isDesign = diagramInfo?.viewDefinition?.type === 'network-design';
  const baselineFolderId = isDesign
    ? diagramInfo?.viewDefinition?.baselineFolderId
    : undefined;

  const { data: folderData } = useFolderData(isDesign ? baselineFolderId : undefined);
  const orgId = folderData?.folder?.orgId;

  const { data: graphData, isLoading: graphLoading, error: graphError } = useCompassGraph(
    isDesign ? baselineFolderId : undefined,
    isDesign ? orgId : undefined,
  );

  const [isHydrated, setIsHydrated] = useState(false);
  const [isHydratingInner, setIsHydratingInner] = useState(false);
  const hydrationLockRef = useRef(false);

  // Effect: when the design is active, the graph is loaded, and the
  // Y.Doc is attached, attempt single-shot hydration.
  useEffect(() => {
    if (!isDesign || !diagramId || !graphData) return;

    const doc = getNodeMutationManager().getDoc();
    if (!doc) {
      // Y.Doc not yet attached — the realtime sync will reattach the
      // mutation manager. This effect re-runs when graphData changes;
      // a separate microtask will see the doc once it's there.
      return;
    }

    // Sentinel check (synchronous): if either flag is set, we're done.
    const meta = doc.getMap<unknown>(META_MAP);
    if (meta.get(META_HYDRATED) === true) {
      setIsHydrated(true);
      return;
    }
    // Short-circuit if shapes already exist — somebody hydrated and
    // forgot to set the flag, or a manual edit beat us.
    if (doc.getMap('shapes').size > 0) {
      setIsHydrated(true);
      return;
    }
    if (hydrationLockRef.current) return;
    hydrationLockRef.current = true;
    setIsHydratingInner(true);

    void (async () => {
      try {
        const importResult = compassGraphToImportResult(graphData.nodes, graphData.edges);
        if (importResult.resources.length === 0) {
          // Empty baseline — still mark hydrated so we don't re-poll.
          doc.transact(() => {
            meta.set(META_HYDRATED, true);
            meta.set(META_HYDRATED_AT, new Date().toISOString());
            if (baselineFolderId) meta.set(META_BASELINE_FOLDER_ID, baselineFolderId);
          }, 'network-design-hydrate-empty');
          setIsHydrated(true);
          return;
        }

        const commandManager = getCommandManager();
        const report = await hydrateImportedResources(importResult, commandManager, {
          commandName: 'Hydrate design from baseline',
        });
        log.info('Design hydration complete', {
          diagramId,
          createdResources: report.createdResourceIds.length,
          createdEdges: report.createdEdgeIds.length,
          warnings: report.warnings.length,
        });

        // Stamp the sentinel inside a single transaction so concurrent
        // viewers see it atomically.
        doc.transact(() => {
          meta.set(META_HYDRATED, true);
          meta.set(META_HYDRATED_AT, new Date().toISOString());
          if (baselineFolderId) meta.set(META_BASELINE_FOLDER_ID, baselineFolderId);
        }, 'network-design-hydrate-complete');

        setIsHydrated(true);
      } catch (err) {
        log.error('Design hydration failed', err as Error);
        hydrationLockRef.current = false; // Permit retry on next graphData change.
      } finally {
        setIsHydratingInner(false);
      }
    })();
  }, [isDesign, diagramId, graphData, baselineFolderId]);

  // Listen for the sentinel landing from a remote collaborator so the
  // UI can flip out of "Seeding…" without polling.
  useEffect(() => {
    if (!isDesign) return;
    const doc = getNodeMutationManager().getDoc();
    if (!doc) return;
    const meta = doc.getMap<unknown>(META_MAP);
    const observer = () => {
      if (meta.get(META_HYDRATED) === true) {
        setIsHydrated(true);
      }
    };
    meta.observe(observer);
    return () => meta.unobserve(observer);
  }, [isDesign, diagramId]);

  return {
    isDesign,
    isHydrating: isDesign && (graphLoading || isHydratingInner) && !isHydrated,
    isHydrated,
    error: graphError ?? null,
  };
}

/**
 * Reads the design metadata from a Y.Doc without subscribing to
 * changes. Exposed so the canvas chrome and the assistant panel can
 * cheaply check hydration status / baseline pointer.
 */
export function readNetworkDesignMeta(doc: Y.Doc): {
  hydrated: boolean;
  hydratedAt: string | null;
  baselineFolderId: string | null;
} {
  const meta = doc.getMap<unknown>(META_MAP);
  return {
    hydrated: meta.get(META_HYDRATED) === true,
    hydratedAt: typeof meta.get(META_HYDRATED_AT) === 'string'
      ? (meta.get(META_HYDRATED_AT) as string)
      : null,
    baselineFolderId: typeof meta.get(META_BASELINE_FOLDER_ID) === 'string'
      ? (meta.get(META_BASELINE_FOLDER_ID) as string)
      : null,
  };
}
